/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import EditIcon from "@material-ui/icons/EditOutlined";
import ErrorIcon from "@material-ui/icons/Error";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import SmsIcon from "@material-ui/icons/SmsOutlined";
import { useI18n } from "hooks/i18n/i18n";
import {
  useLinkPhoneNumber,
  LinkPhoneNumberError,
} from "hooks/useLinkPhoneNumber";
import { useEffect, useRef, useState } from "react";

import { PhoneNumberField, formatPhoneNumber } from "./PhoneNumberField";
import {
  VERIFICATION_CODE_LENGTH,
  VerificationCodeInput,
} from "./VerificationCodeInput";

import type { FC } from "react";

// OTP は input が1つで、6つの枠は見た目だけ。ラベルは枠全体に付ける
const VERIFICATION_CODE_LABEL_ID = "phone-number-verification-code-label";

/**
 * 再送できるようになるまでの秒数。
 * SMSは1通ごとに費用がかかるうえ、連打すると Firebase 側の制限に当たって
 * 正規の利用者まで送れなくなるため、こちら側でも間隔を空ける。
 */
const RESEND_INTERVAL_SECONDS = 60;

/** 再送できるようになるまでの残り秒数。0 なら送れる */
const useRemainingSecondsToResend = (lastSentAt: number | null): number => {
  const [remaining, setRemaining] = useState(0);

  useEffect(() => {
    if (lastSentAt === null) {
      setRemaining(0);
      return;
    }
    const compute = () =>
      Math.max(
        0,
        RESEND_INTERVAL_SECONDS - Math.floor((Date.now() - lastSentAt) / 1000)
      );
    setRemaining(compute());
    const timerId = setInterval(() => {
      const next = compute();
      setRemaining(next);
      if (next === 0) {
        clearInterval(timerId);
      }
    }, 1000);
    return () => clearInterval(timerId);
  }, [lastSentAt]);

  return remaining;
};

const ERROR_MESSAGE_KEY = {
  invalidPhoneNumber: "phoneNumber.errorInvalidPhoneNumber",
  invalidCode: "phoneNumber.errorInvalidCode",
  codeExpired: "phoneNumber.errorCodeExpired",
  alreadyInUse: "phoneNumber.errorAlreadyInUse",
  alreadyLinked: "phoneNumber.errorAlreadyLinked",
  tooManyRequests: "phoneNumber.errorTooManyRequests",
  recaptchaFailed: "phoneNumber.errorRecaptchaFailed",
  requiresRecentLogin: "phoneNumber.errorRequiresRecentLogin",
  unavailable: "phoneNumber.errorUnavailable",
  unknown: "phoneNumber.errorUnknown",
} as const satisfies Record<LinkPhoneNumberError, string>;

type Props = {
  /**
   * invisible reCAPTCHA の描画先の id。要素は呼び出し側が用意する。
   *
   * このフォームが描画すると、フォームを含むツリーが再描画で差し替わったときに
   * 要素ごと消え、チャレンジ中だった reCAPTCHA が描画先を失って壊れる。
   * 画面の寿命と同じだけ存在する位置に置くこと。
   * 同時に複数描画すると壊れるため、同じ画面に2つ置く場合は別の id にする。
   */
  recaptchaContainerId: string;
  /** 何のために電話番号が要るかの説明。置かれる画面で変わるため呼び出し側が決める */
  description: string;
  /**
   * 登録済みの番号を差し替えるときだけ、いまの番号。
   * 与えると新規登録ではなく変更として振る舞う。
   */
  currentPhoneNumber?: string;
  /** 変更をやめたとき。currentPhoneNumber があるときだけ使う */
  onCancel?: () => void;
  /** 紐付けが完了したときに一度だけ呼ばれる */
  onVerified: () => void;
};

/**
 * 電話番号の入力からSMS認証までを行うカード。
 * 登録ページとチケット選択画面で同じ手順を使うために切り出している。
 */
export const PhoneNumberVerificationForm: FC<Props> = ({
  recaptchaContainerId,
  description,
  currentPhoneNumber,
  onCancel,
  onVerified,
}) => {
  const { t } = useI18n();
  const classes = styles();

  const {
    state,
    error,
    isSubmitting,
    lastSent,
    requestVerificationCode,
    confirmVerificationCode,
    backToInput,
  } = useLinkPhoneNumber(recaptchaContainerId);

  // 妥当なときだけ E.164 の番号が入る。検証は intl-tel-input が行う
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isPhoneNumberValid, setIsPhoneNumberValid] = useState(false);
  // 入力途中に赤字を出さないため、フォーカスが外れるか送信を試みるまで伏せる
  const [isPhoneNumberTouched, setIsPhoneNumberTouched] = useState(false);
  const [codeInput, setCodeInput] = useState("");

  // completed のまま再描画されても呼び出しが増えないようにする
  const hasNotifiedRef = useRef(false);
  useEffect(() => {
    if (state.step === "completed" && !hasNotifiedRef.current) {
      hasNotifiedRef.current = true;
      onVerified();
    }
  }, [state.step, onVerified]);

  const remainingSecondsToResend = useRemainingSecondsToResend(
    lastSent?.at ?? null
  );
  // 同じ番号への送り直しは待たせる。番号を直したときは待たせない
  const isResendBlocked =
    remainingSecondsToResend > 0 && lastSent?.phoneNumber === phoneNumber;
  // 送信そのものの失敗で6桁の枠まで赤くしない。枠を赤くするのは入力が誤りのときだけ
  const isCodeError = error === "invalidCode" || error === "codeExpired";
  const isChanging = currentPhoneNumber !== undefined;
  // 妥当でない番号でもボタンは押せるようにする。押せない理由が分からないまま
  // 止まるより、押したときに理由を出す方が迷わない。
  // 押させないのは、送信中と、同じ番号への待ち時間の間だけ
  const isSendDisabled = isSubmitting || isResendBlocked;
  // 6桁そろうまで送らない。桁数不足で弾かれると、入力し直しの手間だけが増える
  const isVerifyDisabled =
    isSubmitting || codeInput.length !== VERIFICATION_CODE_LENGTH;

  return (
    <div css={classes.card}>
      {state.step === "input" ? (
        <form
          css={classes.body}
          onSubmit={(e) => {
            e.preventDefault();
            if (isSendDisabled) return;
            if (!isPhoneNumberValid) {
              setIsPhoneNumberTouched(true);
              return;
            }
            void requestVerificationCode(phoneNumber);
          }}
        >
          <p css={classes.status}>
            {isChanging ? (
              <EditIcon css={classes.statusIcon} />
            ) : (
              <ErrorOutlineIcon css={classes.statusIcon} />
            )}
            <span>
              {isChanging
                ? t("phoneNumber.statusChanging")
                : t("phoneNumber.statusUnverified")}
            </span>
          </p>
          {isChanging ? (
            <div css={classes.current}>
              <p css={classes.currentLabel}>{t("phoneNumber.currentNumber")}</p>
              <p css={classes.currentNumber}>
                {formatPhoneNumber(currentPhoneNumber)}
              </p>
            </div>
          ) : (
            <p css={classes.description}>{description}</p>
          )}
          <div css={classes.notes}>
            {isChanging && (
              <p>{t("phoneNumber.annotationCurrentStaysValid")}</p>
            )}
            <p>{t("phoneNumber.annotationOnePerAccount")}</p>
            {!isChanging && <p>{t("phoneNumber.annotationSms")}</p>}
          </div>
          <hr css={classes.divider} />
          {error !== null && (
            <p css={classes.fieldError}>
              <ErrorIcon css={classes.fieldErrorIcon} />
              <span>{t(ERROR_MESSAGE_KEY[error])}</span>
            </p>
          )}
          <PhoneNumberField
            label={
              isChanging
                ? t("phoneNumber.newInputLabel")
                : t("phoneNumber.inputLabel")
            }
            onChangeNumber={setPhoneNumber}
            onChangeValidity={setIsPhoneNumberValid}
            onBlur={() => setIsPhoneNumberTouched(true)}
            showError={isPhoneNumberTouched && !isPhoneNumberValid}
            disabled={isSubmitting}
          />
          {isResendBlocked && (
            <p css={classes.resend}>
              {t("phoneNumber.resendCountdown", {
                seconds: remainingSecondsToResend,
              })}
            </p>
          )}
          <div css={classes.actions}>
            <button
              type="submit"
              disabled={isSendDisabled}
              css={[
                classes.button,
                isChanging && classes.narrowButton,
                (!isPhoneNumberValid || isResendBlocked) &&
                  classes.disabledButton,
              ]}
            >
              {t("phoneNumber.sendCode")}
            </button>
            {isChanging && onCancel !== undefined && (
              <button
                type="button"
                disabled={isSubmitting}
                onClick={onCancel}
                css={[
                  classes.button,
                  classes.narrowButton,
                  classes.outlineButton,
                ]}
              >
                {t("phoneNumber.cancel")}
              </button>
            )}
          </div>
        </form>
      ) : (
        <form
          css={classes.body}
          onSubmit={(e) => {
            e.preventDefault();
            if (isVerifyDisabled) return;
            void confirmVerificationCode(codeInput);
          }}
        >
          <p css={classes.status}>
            <SmsIcon css={[classes.statusIcon, classes.statusIconSent]} />
            <span>{t("phoneNumber.statusCodeSent")}</span>
          </p>
          <div css={classes.sentTo}>
            {/* この番号はまだ登録されていないため、登録済みのラベルは付けない */}
            <p css={classes.description}>
              {t("phoneNumber.codeSent", {
                phoneNumber:
                  state.step === "codeSent"
                    ? formatPhoneNumber(state.sentPhoneNumber)
                    : "",
              })}
            </p>
            <button
              type="button"
              disabled={isSubmitting}
              onClick={() => {
                setCodeInput("");
                backToInput();
              }}
              css={classes.textButton}
            >
              {t("phoneNumber.resend")}
            </button>
          </div>
          <hr css={classes.divider} />
          <div css={classes.codeField}>
            <p css={classes.label} id={VERIFICATION_CODE_LABEL_ID}>
              {t("phoneNumber.codeInputLabel")}
            </p>
            <VerificationCodeInput
              labelId={VERIFICATION_CODE_LABEL_ID}
              value={codeInput}
              onChange={setCodeInput}
              disabled={isSubmitting}
              isError={isCodeError}
            />
            {error !== null && (
              <p css={classes.fieldError}>
                <ErrorIcon css={classes.fieldErrorIcon} />
                <span>{t(ERROR_MESSAGE_KEY[error])}</span>
              </p>
            )}
            <p css={classes.resend}>
              <span>{t("phoneNumber.resendGuide")}</span>
              {remainingSecondsToResend > 0 ? (
                <span css={classes.resendWaiting}>
                  {t("phoneNumber.resendCountdown", {
                    seconds: remainingSecondsToResend,
                  })}
                </span>
              ) : (
                <button
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => {
                    setCodeInput("");
                    if (state.step === "codeSent") {
                      void requestVerificationCode(state.sentPhoneNumber);
                    }
                  }}
                  css={classes.textButton}
                >
                  {t("phoneNumber.resendCode")}
                </button>
              )}
            </p>
          </div>
          <div css={classes.actions}>
            <button
              type="submit"
              disabled={isVerifyDisabled}
              css={[classes.button, isVerifyDisabled && classes.disabledButton]}
            >
              {t("phoneNumber.verify")}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

const styles = () => {
  return {
    card: css({
      width: "100%",
      maxWidth: "740px",
      padding: "24px",
      backgroundColor: "#fff",
      border: "2px solid #dcdbdc",
    }),
    body: css({
      display: "flex",
      flexDirection: "column",
      gap: "20px",
    }),
    status: css({
      display: "flex",
      margin: 0,
      gap: "10px",
      alignItems: "center",
      fontSize: "18px",
      fontWeight: "bold",
      lineHeight: "25px",
    }),
    statusIcon: css({
      flex: "none",
      fontSize: "24px",
      color: "#6f6f6f",
    }),
    // 送信できた合図なので、注意を促すグレーではなくブランド色にする
    statusIconSent: css({
      color: "#00c2ae",
    }),
    description: css({
      margin: 0,
      fontSize: "14px",
      lineHeight: "22px",
    }),
    notes: css({
      display: "flex",
      p: {
        margin: 0,
      },
      flexDirection: "column",
      gap: "4px",
      color: "#6f6f6f",
      fontSize: "13px",
      lineHeight: "20px",
    }),
    sentTo: css({
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      gap: "6px",
    }),
    divider: css({
      height: "1px",
      margin: 0,
      backgroundColor: "#e5e5e5",
      border: "none",
    }),
    codeField: css({
      display: "flex",
      flexDirection: "column",
      gap: "8px",
    }),
    label: css({
      margin: 0,
      fontSize: "15px",
      lineHeight: "20px",
      fontWeight: 500,
    }),
    fieldError: css({
      display: "flex",
      margin: 0,
      gap: "6px",
      alignItems: "flex-start",
      color: "#e53935",
      fontSize: "13px",
      fontWeight: "bold",
      lineHeight: "20px",
    }),
    fieldErrorIcon: css({
      flex: "none",
      fontSize: "16px",
      // 1行目の文字の中心に合わせる
      marginTop: "2px",
    }),
    resend: css({
      display: "flex",
      margin: 0,
      flexWrap: "wrap",
      alignItems: "center",
      gap: "0 4px",
      paddingTop: "4px",
      color: "#6f6f6f",
      fontSize: "13px",
      lineHeight: "20px",
    }),
    current: css({
      display: "flex",
      flexDirection: "column",
      gap: "2px",
    }),
    currentLabel: css({
      margin: 0,
      color: "#6f6f6f",
      fontSize: "13px",
      lineHeight: "20px",
    }),
    currentNumber: css({
      margin: 0,
      fontSize: "18px",
      fontWeight: "bold",
      lineHeight: "26px",
    }),
    // 変更のときはボタンが2つ並ぶ。狭い画面では横に入らないので縦に積む
    actions: css({
      display: "flex",
      flexDirection: "column",
      gap: "16px",
      alignItems: "center",
      "@media screen and (min-width: 768px)": {
        flexDirection: "row",
        justifyContent: "center",
      },
    }),
    button: css({
      width: "100%",
      maxWidth: "320px",
      padding: "10px 16px",
      color: "#fff",
      fontFamily: "inherit",
      fontSize: "14px",
      fontWeight: "bold",
      lineHeight: "20px",
      backgroundColor: "#00c2ae",
      border: "none",
      cursor: "pointer",
    }),
    // 2つ並ぶときは横幅を分ける
    narrowButton: css({
      maxWidth: "240px",
    }),
    outlineButton: css({
      color: "#0b0b0b",
      backgroundColor: "#fff",
      border: "1px solid #0b0b0b",
    }),
    disabledButton: css({
      backgroundColor: "#d4d4d4",
      cursor: "default",
    }),
    resendWaiting: css({
      color: "#9e9e9e",
      fontWeight: "bold",
    }),
    textButton: css({
      padding: 0,
      color: "#00c2ae",
      fontFamily: "inherit",
      fontSize: "14px",
      fontWeight: "bold",
      lineHeight: "20px",
      textDecoration: "underline",
      backgroundColor: "transparent",
      border: "none",
      cursor: "pointer",
    }),
  };
};
